import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Star, CheckCircle2, Calculator, Languages, Globe } from 'lucide-react';
import { type ModuleData, type ModuleId, MODULES, getFamiliesForModule } from '../../content';
import { ModuleProgress } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection } from '../../mascot';
import { PageHero, HeroStatPill } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { WorldZone } from '../shared/WorldZone';
import { WorldTile } from './WorldTile';
import { hasActiveDecay } from '../../mastery/masteryDecay';
import { getFamilyMasteryKey } from '../../mastery';
// Subject category groupings
const SUBJECT_GROUPS: {
  label: string;
  icon: React.ElementType;
  accentColor: string;
  moduleIds: ModuleId[];
  djayMessage: string;
}[] = [{
  label: 'Rekenen',
  icon: Calculator,
  accentColor: 'bg-blue-100 text-blue-600',
  moduleIds: ['Getalbegrip', 'Basisrekenen', 'Metriek', 'Tijd', 'Geld'],
  djayMessage: 'Laten we rekenen! 🧮'
}, {
  label: 'Taal',
  icon: Languages,
  accentColor: 'bg-rose-100 text-rose-600',
  moduleIds: ['SpellingWW', 'SpellingRegel', 'Taalbegrip'],
  djayMessage: 'Woorden zijn leuk! 📝'
}, {
  label: 'Wereld & Studie',
  icon: Globe,
  accentColor: 'bg-emerald-100 text-emerald-600',
  moduleIds: ['Studievaardig', 'Topo'],
  djayMessage: 'Ontdek de wereld! 🌍'
}];
interface SubjectsPageProps {
  modules: ModuleData[];
  progress: Record<ModuleId, ModuleProgress>;
  selectedGroup: GroupId;
  onModuleSelect: (module: ModuleData) => void;
  onStartPractice: (moduleId: ModuleId) => void;
}
export function SubjectsPage({
  modules,
  progress,
  selectedGroup,
  onModuleSelect,
  onStartPractice
}: SubjectsPageProps) {
  const {
    state,
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const moduleById = Object.fromEntries(modules.map(m => [m.id, m]));
  // Calculate stats for hero
  const masteredCount = MODULES.filter(m => getModuleMasteryForGroup(m.id) >= 95).length;
  const inProgressCount = MODULES.filter(m => {
    const mastery = getModuleMasteryForGroup(m.id);
    const answered = getAnsweredQuestions(m.id).length;
    return (answered > 0 || mastery > 0) && mastery < 95;
  }).length;
  return <PageContainer className="space-y-8 pb-20" data-id="element-2448">
      {/* Hero Header */}
      <motion.div variants={itemVariants} data-id="element-2449">
        <PageHero title="Mijn Werelden" subtitle="Kies een onderwerp om te oefenen en groei als een kampioen! 📚" gradientClasses="from-emerald-500 via-teal-500 to-cyan-500" djayEmotion="pointing" djayVariant={selectedVariant} icon={BookOpen} iconBgClass="bg-emerald-400" floatingEmojis={['📚', '🌟']} data-id="element-2450">
          <HeroStatPill icon={Star} data-id="element-2451">{masteredCount} afgerond</HeroStatPill>
          <HeroStatPill icon={BookOpen} data-id="element-2452">{inProgressCount} bezig</HeroStatPill>
          <HeroStatPill icon={CheckCircle2} data-id="element-2453">Groep {selectedGroup}</HeroStatPill>
        </PageHero>
      </motion.div>

      {/* Grouped Subjects */}
      <div className="space-y-0" data-id="element-2454">
        {SUBJECT_GROUPS.map((group, groupIndex) => {
        const groupModules = group.moduleIds.map(id => moduleById[id]).filter((m): m is ModuleData => !!m);
        if (groupModules.length === 0) return null;
        const isLast = groupIndex === SUBJECT_GROUPS.length - 1;
        return <motion.div key={group.label} variants={itemVariants} data-id="element-2455">
              <WorldZone icon={group.icon} title={group.label} accentColor={group.accentColor} djayMessage={group.djayMessage} djayEmotion={groupIndex === 0 ? 'thinking' : groupIndex === 1 ? 'pointing' : 'excited'} djayVariant={selectedVariant} isLast={isLast} data-id="element-2456">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6 justify-items-center" data-id="element-2457">
                  {groupModules.map((module, index) => {
                const families = getFamiliesForModule(module.id);
                const hasDecay = families.some(f => {
                  const key = getFamilyMasteryKey(f.familyId, selectedGroup);
                  return hasActiveDecay(state.familyMastery?.[key]);
                });
                return <WorldTile key={module.id} module={module} selectedGroup={selectedGroup} onClick={() => onModuleSelect(module)} getModuleMasteryForGroup={getModuleMasteryForGroup} getAnsweredQuestions={getAnsweredQuestions} index={index} hasDecay={hasDecay} data-id="element-2458" />;
              })}
                </div>
              </WorldZone>
            </motion.div>;
      })}
      </div>
    </PageContainer>;
}